/**
 * Payments page — issue #29 + #35
 *
 * Becky's payment surface. Three modes via a tab header:
 *
 *   1. Stripe Connect (account status, "Connect with Stripe" redirect).
 *      Preserves the pre-existing Stripe Connect onboarding card so
 *      tenant owners can complete KYC without leaving the page.
 *
 *   2. Process Payment (active checkout). Customer search → booking
 *      selection → tip + loyalty redemption → choose payment method.
 *      The method set is GATEWAY-RESOLVED (#522): Stripe/simulation
 *      tenants get Card on File / Tap-to-Pay / Manual Entry (Elements
 *      scaffold); Authorize.net tenants get Card on File / Manual Entry
 *      where Manual Entry is the Accept.js keyed form (Tap to Pay does
 *      not exist on that rail — card-present runs through the
 *      first-party Authorize.net app, integration plan §4).
 *
 *   3. Open Invoices queue. Settle-later parking lot — every checked-out
 *      booking without a completed Payment row shows here. Becky can
 *      pick a payment method per invoice; selecting one switches to the
 *      Process Payment tab with the booking pre-loaded.
 *
 * Acceptance criteria coverage (#29):
 *   AC1 — Card on file → ProcessPaymentTab → CardOnFilePayment
 *   AC2 — Tap-to-Pay → ProcessPaymentTab → StripeTerminalPayment
 *   AC3 — Manual entry → ProcessPaymentTab → ManualCardEntry (Stripe Elements scaffold)
 *   AC4 — Open Invoices queue → OpenInvoicesTab → OpenInvoicesQueue
 *
 * Issue #35 (Loyalty redemption) is mounted inside ProcessPaymentTab as
 * a sibling to the tip + payment-method controls. Redemption applies
 * before the payment-method charge — the remainder is settled via the
 * selected method. See LoyaltyRedemption.tsx for the split-payment math.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  adminCrmCustomersApi,
  adminCustomersApi,
  adminPaymentsApi,
  adminTenantStripeApi,
  type AdminOpenInvoice,
  type CustomerSearchResult,
  type TenantStripeStatusResponse,
} from '../lib/api.js';
import { OpenInvoices } from '../components/checkout/OpenInvoices.js';
import { PermissionGate } from '../components/PermissionGate.js';
import { AcceptJsKeyedEntry } from '../components/payments/AcceptJsKeyedEntry.js';
import { CardOnFilePayment } from '../components/payments/CardOnFilePayment.js';
import { EscapedVisits } from '../components/payments/EscapedVisits.js';
import { Transactions } from '../components/payments/Transactions.js';
import { UnresolvedDeskStubs } from '../components/payments/UnresolvedDeskStubs.js';
import { SettlementSettingsCard } from '../components/payments/SettlementSettingsCard.js';
import { GatewayCredentialsCard } from '../components/payments/GatewayCredentialsCard.js';
import {
  LoyaltyRedemption,
  type AppliedRedemption,
} from '../components/payments/LoyaltyRedemption.js';
import { ManualCardEntry } from '../components/payments/ManualCardEntry.js';
import {
  OpenInvoicesQueue,
  type PaymentMethod,
} from '../components/payments/OpenInvoicesQueue.js';
import { StripeTerminalPayment } from '../components/payments/StripeTerminalPayment.js';

type PaymentsProps = {
  config: ClientConfig;
};

type Tab =
  | 'connect'
  | 'process'
  | 'open-invoices'
  | 'transactions'
  | 'gateway';

function handlePaymentsTabKeyDown(
  event: KeyboardEvent<HTMLButtonElement>,
): void {
  const tablist = event.currentTarget.closest('[role="tablist"]');
  const tabs = Array.from(
    tablist?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [],
  );
  const currentIndex = tabs.indexOf(event.currentTarget);
  if (currentIndex < 0 || tabs.length === 0) return;

  let nextIndex: number | null = null;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    nextIndex = (currentIndex + 1) % tabs.length;
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = tabs.length - 1;
  }

  if (nextIndex === null) return;
  event.preventDefault();
  const nextTab = tabs[nextIndex];
  if (!nextTab) return;
  nextTab.focus();
  nextTab.click();
}

type ConnectState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; isRetryable: boolean }
  | { kind: 'ready'; status: TenantStripeStatusResponse };

type ConnectAction =
  | { kind: 'idle' }
  | { kind: 'minting' }
  | { kind: 'redirecting'; url: string }
  | { kind: 'error'; message: string };

type CheckoutInvoice = {
  bookingId: string;
  customerId: string;
  customerName: string;
  totalCents: number;
  /**
   * Server-of-record OpenInvoice id when the checkout was loaded from the
   * open-invoices queue (#252 — lets a redemption-only success settle the
   * row so it can't be double-collected at end of day). Null when the
   * checkout context arrived without a queue row.
   */
  invoiceId: string | null;
};

/**
 * #252 — the customer's REAL loyalty balance for the settle flow. The old
 * code fabricated `points_balance: 0` for queue-selected customers, which
 * made every tier render "needs N more points" (loyalty-at-settle dead).
 * We now fetch the real balance; when it cannot be read we show NOTHING
 * rather than a fabricated number.
 */
type LoyaltyBalanceState =
  | { kind: 'loading' }
  | { kind: 'ready'; balance: number }
  | { kind: 'unavailable' };

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function describeAccountStatus(status: string | null): {
  label: string;
  tone: 'success' | 'pending' | 'warning' | 'neutral';
} {
  if (status == null) {
    return { label: 'Not connected', tone: 'neutral' };
  }
  switch (status) {
    case 'active':
      return { label: 'Active', tone: 'success' };
    case 'onboarding':
      return { label: 'Onboarding in progress', tone: 'pending' };
    case 'verification_required':
      return { label: 'Verification required', tone: 'warning' };
    case 'restricted':
      return { label: 'Restricted', tone: 'warning' };
    case 'disconnected':
      return { label: 'Disconnected', tone: 'warning' };
    default:
      return { label: status, tone: 'neutral' };
  }
}

export function Payments({ config }: PaymentsProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [tab, setTab] = useState<Tab>('process');
  // Latest checkout-context customer id — staleness guard for the async
  // loyalty-balance read (#252).
  const checkoutCustomerIdRef = useRef<string | null>(null);

  // ── Stripe Connect status (preserved from prior implementation) ────────
  const [connect, setConnect] = useState<ConnectState>({ kind: 'loading' });
  const [connectAction, setConnectAction] = useState<ConnectAction>({
    kind: 'idle',
  });

  const loadConnectStatus = useCallback(async () => {
    setConnect({ kind: 'loading' });
    const result = await adminTenantStripeApi.getStatus(apiUrl);
    if (result.error || !result.data) {
      setConnect({
        kind: 'error',
        message: result.error ?? 'Could not load Stripe status',
        isRetryable: result.isRetryable === true,
      });
      return;
    }
    setConnect({ kind: 'ready', status: result.data });
  }, [apiUrl]);

  useEffect(() => {
    void loadConnectStatus();
  }, [loadConnectStatus]);

  const handleStripeConnect = useCallback(async () => {
    setConnectAction({ kind: 'minting' });
    const returnUrl =
      typeof window !== 'undefined' ? window.location.href : '';
    const result = await adminTenantStripeApi.createAccountLink(
      apiUrl,
      returnUrl,
      returnUrl,
    );
    if (result.error || !result.data) {
      setConnectAction({
        kind: 'error',
        message: result.error ?? 'Could not generate a Stripe onboarding link',
      });
      return;
    }
    setConnectAction({ kind: 'redirecting', url: result.data.url });
    void loadConnectStatus();
    if (typeof window !== 'undefined') {
      window.location.href = result.data.url;
    }
  }, [apiUrl, loadConnectStatus]);

  const statusBadge = useMemo(() => {
    if (connect.kind !== 'ready') return null;
    return describeAccountStatus(connect.status.stripeAccountStatus);
  }, [connect]);

  // #492 — the Stripe Connect tab is a Stripe-only, REAL-money onboarding
  // surface. An Authorize.net merchant (HTHD) or a tenant on any non-Stripe
  // gateway must not see it; NOR may a SIMULATION-gateway tenant (Switchback,
  // whose config carries the DB-default 'stripe') — it would put a real Stripe
  // onboarding CTA into Pat's sales demos. Gate on BOTH the configured gateway
  // AND the resolved execution mode (getStatus now returns `gatewayMode`).
  // Hidden until status resolves, on any non-'stripe' gateway, and whenever the
  // mode is 'simulation' (or absent — an un-upgraded server → safe default).
  // A genuine live/test Stripe tenant still gets the tab (connected or not).
  const showStripeConnectTab =
    connect.kind === 'ready' &&
    connect.status.gateway === 'stripe' &&
    connect.status.gatewayMode !== 'simulation' &&
    connect.status.gatewayMode !== undefined;

  // #522 — the desk method set is gateway-resolved. On the Authorize.net
  // rail the physical-card path is Accept.js keyed entry (card-not-
  // present) and Tap to Pay does not exist (integration plan §4:
  // card-present runs through the first-party Authorize.net app) — so
  // AuthNet tenants get exactly Card on file + Manual entry. Stripe AND
  // simulation tenants keep the pre-existing three methods. The reported
  // `gateway` alone IS the resolved signal here (the #492 residual class
  // is covered): a simulation tenant carrying the DB-default config
  // reports gateway 'stripe'/null → standard rail, and the desk NEVER
  // runs simulation for an authorizenet selection — resolveDeskRuntime
  // (payments-admin/routes.ts) fails CLOSED on authorizenet+unprovisioned,
  // so routing that state to the keyed surface (whose gateway-config read
  // renders the honest not-configured teaching card) beats offering a sim
  // scaffold whose charge would 503. While the status read is in flight
  // NO method renders (resolved set only — no flash of a wrong method); a
  // status ERROR falls back to the pre-#522 three so a transient read
  // failure can't dead-end the desk — the server's typed guards own the
  // money path either way.
  const gatewayRail: 'loading' | 'authorizenet' | 'standard' =
    connect.kind === 'loading'
      ? 'loading'
      : connect.kind === 'ready' && connect.status.gateway === 'authorizenet'
        ? 'authorizenet'
        : 'standard';

  // ── Process Payment state ──────────────────────────────────────────────
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState<
    CustomerSearchResult[]
  >([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerSearchError, setCustomerSearchError] = useState<string | null>(
    null,
  );
  // #271 — track whether a search has completed so "no results" is
  // visible rather than silently empty (the server fix is WS-D; this is
  // the client-side UX fix so the desk staff aren't left wondering).
  const [customerSearchDone, setCustomerSearchDone] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerSearchResult | null>(null);
  const [activeInvoice, setActiveInvoice] = useState<CheckoutInvoice | null>(
    null,
  );
  const [tipDollars, setTipDollars] = useState('');
  const [appliedRedemption, setAppliedRedemption] =
    useState<AppliedRedemption | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [paymentSuccessId, setPaymentSuccessId] = useState<string | null>(null);
  const [invoicesRefreshKey, setInvoicesRefreshKey] = useState(0);
  // #252 — real loyalty balance for the settle flow (never fabricated).
  const [loyaltyBalance, setLoyaltyBalance] = useState<LoyaltyBalanceState>({
    kind: 'unavailable',
  });
  // #252 — redemption-only settles must mark the queue row settled; when
  // that follow-up fails, staff get an explicit do-not-collect-again note.
  const [redemptionSettleNotice, setRedemptionSettleNotice] = useState<
    string | null
  >(null);

  const handleCustomerSearch = useCallback(async () => {
    const q = customerQuery.trim();
    if (!q) {
      setCustomerSearchError('Enter a name, phone, or email to search.');
      return;
    }
    setCustomerSearching(true);
    setCustomerSearchError(null);
    setCustomerSearchDone(false);
    const result = await adminCustomersApi.search(apiUrl, q);
    setCustomerSearching(false);
    setCustomerSearchDone(true);
    if (result.error) {
      setCustomerSearchError(result.error);
      setCustomerResults([]);
      return;
    }
    setCustomerResults(result.data?.customers ?? []);
  }, [apiUrl, customerQuery]);

  const handleSelectCustomer = useCallback((c: CustomerSearchResult) => {
    checkoutCustomerIdRef.current = c.id;
    setSelectedCustomer(c);
    setCustomerResults([]);
    setCustomerQuery('');
    setCustomerSearchDone(false);
    setActiveInvoice(null);
    setAppliedRedemption(null);
    setSelectedMethod(null);
    setPaymentSuccessId(null);
    setRedemptionSettleNotice(null);
    // The search endpoint returns the real balance — usable immediately.
    setLoyaltyBalance({ kind: 'ready', balance: c.points_balance });
  }, []);

  const handleClearCheckout = useCallback(() => {
    checkoutCustomerIdRef.current = null;
    setSelectedCustomer(null);
    setCustomerResults([]);
    setCustomerQuery('');
    setCustomerSearchDone(false);
    setActiveInvoice(null);
    setAppliedRedemption(null);
    setSelectedMethod(null);
    setPaymentSuccessId(null);
    setTipDollars('');
    setLoyaltyBalance({ kind: 'unavailable' });
    setRedemptionSettleNotice(null);
  }, []);

  const handleSelectFromQueue = useCallback(
    (invoice: AdminOpenInvoice, method: PaymentMethod) => {
      setSelectedCustomer({
        id: invoice.customerId,
        first_name: '',
        last_name: '',
        name: invoice.customerName,
        phone: invoice.customerPhone ?? '',
        email: '',
        // Placeholder until the real balance read below resolves — the
        // loyalty UI keys off `loyaltyBalance`, NEVER off this field (#252).
        points_balance: 0,
      });
      setActiveInvoice({
        bookingId: invoice.bookingId,
        customerId: invoice.customerId,
        customerName: invoice.customerName,
        totalCents: invoice.totalCents,
        invoiceId: invoice.id,
      });
      setSelectedMethod(method);
      setAppliedRedemption(null);
      setPaymentSuccessId(null);
      setRedemptionSettleNotice(null);
      setTab('process');

      // #252 — read the customer's REAL loyalty balance. On failure the
      // loyalty section shows an "unavailable" note instead of a fabricated
      // 0-points balance. Guarded by customer id so a stale response can't
      // clobber a newer selection.
      // BLOCKER-2 review fix: this read previously hit the PHANTOM
      // GET /api/admin/customers/:id (no server route → deny-by-default
      // 403 → loyalty dead in prod). It now hits the REAL CRM route,
      // GET /api/admin/crm/customers/:id, and unwraps its { customer }
      // envelope (customerSummaryView exposes points_balance).
      checkoutCustomerIdRef.current = invoice.customerId;
      setLoyaltyBalance({ kind: 'loading' });
      void adminCrmCustomersApi.get(apiUrl, invoice.customerId).then((res) => {
        // Another selection happened meanwhile — leave everything alone.
        if (checkoutCustomerIdRef.current !== invoice.customerId) return;
        const balance = res.data?.customer?.points_balance;
        if (res.error || typeof balance !== 'number') {
          setLoyaltyBalance({ kind: 'unavailable' });
          return;
        }
        setLoyaltyBalance({ kind: 'ready', balance });
        setSelectedCustomer((prev) =>
          prev && prev.id === invoice.customerId
            ? { ...prev, points_balance: balance }
            : prev,
        );
      });
    },
    [apiUrl],
  );

  const handlePaymentSuccess = useCallback((paymentId: string) => {
    setPaymentSuccessId(paymentId);
    // Bump the open-invoices key so when the staff jumps back to that
    // tab the queue refreshes and the just-settled row disappears.
    setInvoicesRefreshKey((k) => k + 1);
  }, []);

  const parsedTipDollars = parseFloat(tipDollars);
  const tipCents = Number.isFinite(parsedTipDollars) && parsedTipDollars >= 0
    ? Math.round(parsedTipDollars * 100)
    : 0;

  const baseTotalCents = activeInvoice?.totalCents ?? 0;
  const discountCents = appliedRedemption?.valueCents ?? 0;
  const dueAfterRedemption = Math.max(0, baseTotalCents - discountCents);
  const dueWithTip = dueAfterRedemption + tipCents;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <section className="ferro-admin-page" aria-busy={connect.kind === 'loading'}>
      <h1 className="ferro-section__title">Payments</h1>

      <div
        role="tablist"
        aria-label="Payments sections"
        data-testid="payments-tablist"
        style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <button
          id="payments-tab-process"
          type="button"
          role="tab"
          aria-selected={tab === 'process'}
          aria-controls="payments-panel-process"
          tabIndex={tab === 'process' ? 0 : -1}
          className={`ferro-btn ${
            tab === 'process' ? 'ferro-btn--primary' : 'ferro-btn--ghost'
          }`}
          onClick={() => setTab('process')}
          onKeyDown={handlePaymentsTabKeyDown}
          data-testid="payments-tab-process"
        >
          Process payment
        </button>
        <button
          id="payments-tab-open-invoices"
          type="button"
          role="tab"
          aria-selected={tab === 'open-invoices'}
          aria-controls="payments-panel-open-invoices"
          tabIndex={tab === 'open-invoices' ? 0 : -1}
          className={`ferro-btn ${
            tab === 'open-invoices' ? 'ferro-btn--primary' : 'ferro-btn--ghost'
          }`}
          onClick={() => setTab('open-invoices')}
          onKeyDown={handlePaymentsTabKeyDown}
          data-testid="payments-tab-open-invoices"
        >
          Settleable bookings
        </button>
        <button
          id="payments-tab-transactions"
          type="button"
          role="tab"
          aria-selected={tab === 'transactions'}
          aria-controls="payments-panel-transactions"
          tabIndex={tab === 'transactions' ? 0 : -1}
          className={`ferro-btn ${
            tab === 'transactions' ? 'ferro-btn--primary' : 'ferro-btn--ghost'
          }`}
          onClick={() => setTab('transactions')}
          onKeyDown={handlePaymentsTabKeyDown}
          data-testid="payments-tab-transactions"
        >
          Transactions
        </button>
        {showStripeConnectTab ? (
          <button
            id="payments-tab-connect"
            type="button"
            role="tab"
            aria-selected={tab === 'connect'}
            aria-controls="payments-panel-connect"
            tabIndex={tab === 'connect' ? 0 : -1}
            className={`ferro-btn ${
              tab === 'connect' ? 'ferro-btn--primary' : 'ferro-btn--ghost'
            }`}
            onClick={() => setTab('connect')}
            onKeyDown={handlePaymentsTabKeyDown}
            data-testid="payments-tab-connect"
          >
            Stripe Connect
          </button>
        ) : null}
        {/* #233 — owner-only gateway credential entry/rotation. The gate is
            a UX courtesy; the server's deny-by-default guard enforces
            payments-ops.credentials:write:all regardless. */}
        <PermissionGate permission="payments-ops.credentials:write:all">
          <button
            id="payments-tab-gateway"
            type="button"
            role="tab"
            aria-selected={tab === 'gateway'}
            aria-controls="payments-panel-gateway"
            tabIndex={tab === 'gateway' ? 0 : -1}
            className={`ferro-btn ${
              tab === 'gateway' ? 'ferro-btn--primary' : 'ferro-btn--ghost'
            }`}
            onClick={() => setTab('gateway')}
            onKeyDown={handlePaymentsTabKeyDown}
            data-testid="payments-tab-gateway"
          >
            Gateway credentials
          </button>
        </PermissionGate>
      </div>

      {tab === 'connect' ? (
        <div
          id="payments-panel-connect"
          role="tabpanel"
          aria-labelledby="payments-tab-connect"
          tabIndex={0}
        >
          <p className="ferro-muted">
            Connect your Stripe account so {config.brand.name} can process
            bookings, deposits, and payouts directly. Funds settle to your
            Stripe-linked bank account on Stripe's standard payout schedule.
          </p>

          {connect.kind === 'loading' ? (
            <p className="ferro-muted" role="status">
              Loading Stripe status…
            </p>
          ) : null}

          {connect.kind === 'error' ? (
            <div className="ferro-dashboard__error" role="alert">
              <p>{connect.message}</p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => void loadConnectStatus()}
              >
                Retry
              </button>
            </div>
          ) : null}

          {connect.kind === 'ready' && statusBadge ? (
            <article
              className="ferro-card"
              aria-label="Stripe Connect account status"
            >
              <header className="ferro-card__header">
                <h2 className="ferro-section__subtitle">Account status</h2>
                <span
                  className={`ferro-badge ferro-badge--${statusBadge.tone}`}
                  data-testid="payments-status-badge"
                >
                  {statusBadge.label}
                </span>
              </header>

              <dl
                className="ferro-intake__statusGrid"
                data-testid="payments-status-grid"
              >
                <div>
                  <dt>Stripe account</dt>
                  <dd data-testid="payments-account-id">
                    {connect.status.stripeAccountId ? (
                      <code>{connect.status.stripeAccountId}</code>
                    ) : (
                      <span className="ferro-muted">Not yet connected</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Charges enabled</dt>
                  <dd data-testid="payments-charges-enabled">
                    {connect.status.chargesEnabled ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt>Payouts enabled</dt>
                  <dd data-testid="payments-payouts-enabled">
                    {connect.status.payoutsEnabled ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt>Details submitted</dt>
                  <dd data-testid="payments-details-submitted">
                    {connect.status.detailsSubmitted ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt>Last updated</dt>
                  <dd data-testid="payments-updated-at">
                    {connect.status.updatedAt
                      ? new Date(connect.status.updatedAt).toLocaleString()
                      : '—'}
                  </dd>
                </div>
              </dl>

              <footer className="ferro-card__footer">
                <button
                  type="button"
                  className="ferro-btn ferro-btn--primary"
                  onClick={() => void handleStripeConnect()}
                  disabled={
                    connectAction.kind === 'minting' ||
                    connectAction.kind === 'redirecting'
                  }
                  data-testid="payments-connect-button"
                >
                  {connectAction.kind === 'minting'
                    ? 'Generating link…'
                    : connectAction.kind === 'redirecting'
                      ? 'Redirecting to Stripe…'
                      : connect.status.stripeAccountId
                        ? 'Continue Stripe onboarding'
                        : 'Connect with Stripe'}
                </button>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => void loadConnectStatus()}
                  disabled={connectAction.kind === 'minting'}
                  data-testid="payments-refresh-button"
                >
                  Refresh status
                </button>
              </footer>

              {connectAction.kind === 'error' ? (
                <p
                  role="alert"
                  className="ferro-form__error"
                  data-testid="payments-connect-error"
                >
                  {connectAction.message}
                </p>
              ) : null}
            </article>
          ) : null}
        </div>
      ) : null}

      {tab === 'gateway' ? (
        <PermissionGate permission="payments-ops.credentials:write:all">
          <div
            id="payments-panel-gateway"
            role="tabpanel"
            aria-labelledby="payments-tab-gateway"
            tabIndex={0}
            data-testid="payments-gateway-panel"
          >
            <GatewayCredentialsCard apiUrl={apiUrl} />
            <SettlementSettingsCard apiUrl={apiUrl} />
          </div>
        </PermissionGate>
      ) : null}

      {tab === 'process' ? (
        <div
          id="payments-panel-process"
          role="tabpanel"
          aria-labelledby="payments-tab-process"
          tabIndex={0}
          data-testid="payments-process-panel"
        >
          {/* Customer + booking context ── */}
          {!selectedCustomer ? (
            <article
              className="ferro-card"
              aria-label="Customer lookup"
              data-testid="customer-lookup"
            >
              <header className="ferro-card__header">
                <h2 className="ferro-section__subtitle">Find customer</h2>
              </header>
              <div className="ferro-form">
                <label htmlFor="payment-customer-search">
                  Search by name, phone, or email
                </label>
                <input
                  id="payment-customer-search"
                  type="search"
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  placeholder="Ada Lovelace · 555-0101 · ada@example.com"
                  data-testid="customer-search-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleCustomerSearch();
                  }}
                />
                <div className="ferro-form__actions">
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary"
                    onClick={() => void handleCustomerSearch()}
                    disabled={customerSearching}
                    data-testid="customer-search-button"
                  >
                    {customerSearching ? 'Searching…' : 'Search'}
                  </button>
                </div>
                {customerSearchError ? (
                  <p className="ferro-form__error" role="alert">
                    {customerSearchError}
                  </p>
                ) : null}
                {customerResults.length > 0 ? (
                  <ul
                    data-testid="customer-search-results"
                    style={{ listStyle: 'none', padding: 0 }}
                  >
                    {customerResults.map((c) => (
                      <li
                        key={c.id}
                        style={{
                          padding: '0.5rem 0',
                          borderBottom: '1px solid var(--ferro-color-border, #eee)',
                        }}
                      >
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--ghost"
                          onClick={() => handleSelectCustomer(c)}
                          data-testid={`customer-select-${c.id}`}
                        >
                          {c.name} · {c.phone || c.email || '—'} ·{' '}
                          {c.points_balance.toLocaleString('en-US')} pts
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {/* #271 — show a clear no-results message instead of
                    silent emptiness when the search completed but
                    matched no customers. The server-side fix (WS-D)
                    addresses why prod data may be empty; this ensures
                    the UI surface is never silently ambiguous. */}
                {customerSearchDone &&
                  !customerSearching &&
                  !customerSearchError &&
                  customerResults.length === 0 ? (
                  <p
                    className="ferro-muted"
                    data-testid="customer-search-empty"
                    role="status"
                    style={{ marginTop: '8px', fontSize: '0.9rem' }}
                  >
                    No customers found for &ldquo;{customerQuery}&rdquo;. Try a
                    different name, email, or phone number.
                  </p>
                ) : null}
              </div>
            </article>
          ) : null}

          {selectedCustomer && !activeInvoice ? (
            <article
              className="ferro-card"
              aria-label="Booking lookup"
              data-testid="booking-lookup"
            >
              <header className="ferro-card__header">
                <h2 className="ferro-section__subtitle">
                  Selected customer: {selectedCustomer.name}
                </h2>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={handleClearCheckout}
                  data-testid="customer-clear"
                >
                  Change customer
                </button>
              </header>
              <p className="ferro-muted">
                To process a payment, open the customer's booking from the
                Bookings page and use the Pay action, or select an open
                invoice from the Open Invoices tab.
              </p>
            </article>
          ) : null}

          {selectedCustomer && activeInvoice ? (
            <>
              <article
                className="ferro-card"
                aria-label="Checkout summary"
                data-testid="checkout-summary"
              >
                <header className="ferro-card__header">
                  <h2 className="ferro-section__subtitle">Checkout</h2>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    onClick={handleClearCheckout}
                    data-testid="checkout-clear"
                  >
                    Cancel
                  </button>
                </header>
                <dl className="ferro-intake__statusGrid">
                  <div>
                    <dt>Customer</dt>
                    <dd data-testid="checkout-customer">
                      {activeInvoice.customerName}
                    </dd>
                  </div>
                  <div>
                    <dt>Booking</dt>
                    <dd data-testid="checkout-booking-id">
                      <code>{activeInvoice.bookingId.slice(0, 8)}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>Subtotal</dt>
                    <dd data-testid="checkout-subtotal">
                      {formatMoney(baseTotalCents)}
                    </dd>
                  </div>
                  {appliedRedemption ? (
                    <div>
                      <dt>Loyalty discount</dt>
                      <dd data-testid="checkout-redemption">
                        −{formatMoney(discountCents)}
                      </dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Tip</dt>
                    <dd>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={tipDollars}
                        onChange={(e) => setTipDollars(e.target.value)}
                        placeholder="0.00"
                        data-testid="checkout-tip-input"
                        style={{ width: '6rem' }}
                      />
                      {tipCents > 0 ? (
                        <span className="ferro-muted">
                          {' '}
                          ({formatMoney(tipCents)})
                        </span>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt>Total due</dt>
                    <dd data-testid="checkout-total-due">
                      <strong>{formatMoney(dueWithTip)}</strong>
                    </dd>
                  </div>
                </dl>
              </article>

              {/* #252 — loyalty keys off the REAL fetched balance. While it
                  loads we show a status line; when it cannot be read we show
                  an explicit unavailable note — never a fabricated 0. */}
              {loyaltyBalance.kind === 'loading' ? (
                <p
                  className="ferro-muted"
                  role="status"
                  data-testid="loyalty-balance-loading"
                >
                  Loading loyalty balance…
                </p>
              ) : null}
              {loyaltyBalance.kind === 'unavailable' ? (
                <p
                  className="ferro-muted"
                  data-testid="loyalty-balance-unavailable"
                >
                  Loyalty balance unavailable — points redemption can’t be
                  offered for this checkout.
                </p>
              ) : null}
              {loyaltyBalance.kind === 'ready' ? (
                <LoyaltyRedemption
                  apiUrl={apiUrl}
                  customer={{
                    id: selectedCustomer.id,
                    name: selectedCustomer.name,
                    pointsBalance: loyaltyBalance.balance,
                  }}
                  applied={appliedRedemption}
                  onRedemptionApplied={(applied) => {
                    setAppliedRedemption(applied);
                    // Reflect new balance back into the selected customer
                    setLoyaltyBalance({
                      kind: 'ready',
                      balance: applied.newBalance,
                    });
                    setSelectedCustomer((prev) =>
                      prev
                        ? { ...prev, points_balance: applied.newBalance }
                        : prev,
                    );
                    // Redemption-only flow: when points cover the full total
                    // and the staff hasn't added a tip, mark the payment
                    // complete immediately with the redemption id. No
                    // additional payment-method round-trip required.
                    if (
                      applied.valueCents >= baseTotalCents &&
                      tipCents === 0
                    ) {
                      setPaymentSuccessId(
                        `redemption:${applied.redemptionCode}`,
                      );
                      // #252 — the points are burned; the queue row must be
                      // settled too or end-of-day staff will collect AGAIN.
                      // should-fix 6: settledVia 'redemption' makes the
                      // server mint a ZERO-value ledger row instead of a
                      // phantom full-total desk Payment (points-covered
                      // revenue must not double-record).
                      const invoiceId = activeInvoice?.invoiceId ?? null;
                      if (invoiceId) {
                        void adminPaymentsApi
                          .settleOpenInvoice(apiUrl, invoiceId, {
                            settledVia: 'redemption',
                            redemptionCode: applied.redemptionCode,
                          })
                          .then((res) => {
                            if (res.error || !res.data?.invoice) {
                              setRedemptionSettleNotice(
                                `Points were redeemed, but the open invoice could not be marked settled (${res.error ?? 'unknown error'}). Do NOT collect again — settle the row from the queue; it links to this redemption.`,
                              );
                              return;
                            }
                            setInvoicesRefreshKey((k) => k + 1);
                          });
                      }
                    }
                  }}
                  onRedemptionCleared={() => setAppliedRedemption(null)}
                  checkoutTotalCents={baseTotalCents}
                />
              ) : null}

              {redemptionSettleNotice ? (
                <div
                  className="ferro-dashboard__error"
                  role="alert"
                  data-testid="redemption-settle-notice"
                >
                  <p>{redemptionSettleNotice}</p>
                </div>
              ) : null}

              {!paymentSuccessId ? (
                <article
                  className="ferro-card"
                  aria-label="Payment method"
                  data-testid="payment-method-card"
                >
                  <header className="ferro-card__header">
                    <h3 className="ferro-section__subtitle">Payment method</h3>
                  </header>
                  {/* #522 — render the RESOLVED method set only: no
                      button may flash and then disappear (or worse,
                      offer a method this gateway can't run). */}
                  {gatewayRail === 'loading' ? (
                    <p
                      className="ferro-muted"
                      role="status"
                      data-testid="payment-methods-loading"
                    >
                      Loading payment methods…
                    </p>
                  ) : (
                    <div
                      role="radiogroup"
                      aria-label="Choose payment method"
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selectedMethod === 'card-on-file'}
                        className={`ferro-btn ${
                          selectedMethod === 'card-on-file'
                            ? 'ferro-btn--primary'
                            : 'ferro-btn--ghost'
                        }`}
                        onClick={() => setSelectedMethod('card-on-file')}
                        data-testid="method-card-on-file"
                      >
                        Card on file
                      </button>
                      {gatewayRail !== 'authorizenet' ? (
                        <button
                          type="button"
                          role="radio"
                          aria-checked={selectedMethod === 'tap-to-pay'}
                          className={`ferro-btn ${
                            selectedMethod === 'tap-to-pay'
                              ? 'ferro-btn--primary'
                              : 'ferro-btn--ghost'
                          }`}
                          onClick={() => setSelectedMethod('tap-to-pay')}
                          data-testid="method-tap-to-pay"
                        >
                          Tap to pay
                        </button>
                      ) : null}
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selectedMethod === 'manual'}
                        className={`ferro-btn ${
                          selectedMethod === 'manual'
                            ? 'ferro-btn--primary'
                            : 'ferro-btn--ghost'
                        }`}
                        onClick={() => setSelectedMethod('manual')}
                        data-testid="method-manual"
                      >
                        Manual entry
                      </button>
                    </div>
                  )}
                </article>
              ) : null}

              {!paymentSuccessId && selectedMethod === 'card-on-file' ? (
                <CardOnFilePayment
                  apiUrl={apiUrl}
                  customerId={activeInvoice.customerId}
                  bookingId={activeInvoice.bookingId}
                  totalCents={dueAfterRedemption}
                  tipCents={tipCents}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setSelectedMethod(null)}
                />
              ) : null}

              {/* #522 — Tap to Pay does not exist on the AuthNet rail; a
                  queue pre-selection that raced the status read falls
                  through to the keyed form (a dead-end 501 is a defect,
                  the obvious next action for a physical card is keying
                  it). */}
              {!paymentSuccessId &&
              selectedMethod === 'tap-to-pay' &&
              gatewayRail !== 'authorizenet' ? (
                <StripeTerminalPayment
                  apiUrl={apiUrl}
                  bookingId={activeInvoice.bookingId}
                  totalCents={dueAfterRedemption}
                  tipCents={tipCents}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setSelectedMethod(null)}
                />
              ) : null}

              {!paymentSuccessId &&
              (selectedMethod === 'manual' ||
                (selectedMethod === 'tap-to-pay' &&
                  gatewayRail === 'authorizenet')) ? (
                gatewayRail === 'authorizenet' ? (
                  <AcceptJsKeyedEntry
                    apiUrl={apiUrl}
                    bookingId={activeInvoice.bookingId}
                    totalCents={dueAfterRedemption}
                    tipCents={tipCents}
                    onSuccess={handlePaymentSuccess}
                    onCancel={() => setSelectedMethod(null)}
                  />
                ) : (
                  <ManualCardEntry
                    apiUrl={apiUrl}
                    bookingId={activeInvoice.bookingId}
                    totalCents={dueAfterRedemption}
                    tipCents={tipCents}
                    onSuccess={handlePaymentSuccess}
                    onCancel={() => setSelectedMethod(null)}
                  />
                )
              ) : null}

              {paymentSuccessId ? (
                <article
                  className="ferro-card"
                  aria-label="Payment success"
                  data-testid="payment-success"
                >
                  <header className="ferro-card__header">
                    <h3 className="ferro-section__subtitle">
                      Payment complete
                    </h3>
                    <span className="ferro-badge ferro-badge--success">
                      Paid
                    </span>
                  </header>
                  <p>
                    Payment <code>{paymentSuccessId}</code> recorded for{' '}
                    {activeInvoice.customerName} ·{' '}
                    {formatMoney(dueWithTip)}.
                  </p>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary"
                    onClick={handleClearCheckout}
                    data-testid="payment-success-new"
                  >
                    Take next payment
                  </button>
                </article>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {tab === 'open-invoices' ? (
        <div
          id="payments-panel-open-invoices"
          role="tabpanel"
          aria-labelledby="payments-tab-open-invoices"
          tabIndex={0}
          data-testid="payments-open-invoices-panel"
        >
          <p className="ferro-muted">
            Bookings that have been checked out but not yet paid. Pick a
            payment method per invoice — selecting one switches to the
            Process Payment tab with the booking pre-loaded.
          </p>
          {/* #251 should-fix 4 — escaped-visits reconciliation: checked-out
              bookings with NO money record at all (crash window between the
              check-out POST and the invoice POST). Renders nothing when the
              list is empty. Recording a visit re-queues its invoice, so we
              bump the shared refresh key to refresh the queue below. */}
          <EscapedVisits
            apiUrl={apiUrl}
            refreshKey={invoicesRefreshKey}
            onRecorded={() => setInvoicesRefreshKey((k) => k + 1)}
          />
          {/* Issue #29 — server-driven Open Invoices queue. Lists
              bookings with status in (checked_out, completed) without
              a completed Payment row. #522: the per-row method buttons
              carry the gateway-resolved set (no Tap to Pay on AuthNet). */}
          <OpenInvoicesQueue
            apiUrl={apiUrl}
            onSelectInvoice={handleSelectFromQueue}
            refreshKey={invoicesRefreshKey}
            {...(gatewayRail === 'authorizenet'
              ? { methods: ['card-on-file', 'manual'] as const }
              : {})}
          />
        </div>
      ) : null}

      {tab === 'transactions' ? (
        <div
          id="payments-panel-transactions"
          role="tabpanel"
          aria-labelledby="payments-tab-transactions"
          tabIndex={0}
          data-testid="payments-panel-transactions"
        >
          {/* #572 — unresolved pending desk stubs (gateway timeout/duplicate
              stranded the booking). Renders nothing when the list is empty;
              resolving one frees the booking and refreshes the queue. */}
          <UnresolvedDeskStubs
            apiUrl={apiUrl}
            refreshKey={invoicesRefreshKey}
            onResolved={() => setInvoicesRefreshKey((k) => k + 1)}
          />
          {/* Money history + the Void/Refund home (Authorize.net virtual-
              terminal parity). Settlement state drives Void-vs-Refund per
              the per-tenant batch-time clock heuristic. */}
          <Transactions
            apiUrl={apiUrl}
            refreshKey={invoicesRefreshKey}
            onChanged={() => setInvoicesRefreshKey((k) => k + 1)}
          />
        </div>
      ) : null}

      {/* Issue #25 — Open Invoices queue. Renders bookings that were
        * checked out via "Settle Later" so staff can batch-settle at
        * end of day. Storage is currently client-side localStorage;
        * swap to server-of-record when the Track-A endpoint lands.
        *
        * Mounted outside the tab system so end-of-day settling is one
        * scroll away on any visit to /payments — staff don't need to
        * remember to click the Open Invoices tab. */}
      <OpenInvoices apiUrl={apiUrl} refreshKey={invoicesRefreshKey} />
    </section>
  );
}
